import { ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SERVICE_TYPES, formatDate, formatMoney } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { ServiceRow, StatusBadge } from "@/components/app/ServiceRow";

export const SORTS = [
  ["data", "Data (mais cedo primeiro)"], ["valor-asc", "Valor: menor → maior"], ["valor-desc", "Valor: maior → menor"],
  ["az", "Cliente: A → Z"], ["za", "Cliente: Z → A"],
];

const priceOf = (s) => (s.price == null || s.price === "" ? null : Number(s.price));
const byDate = (a, b) => a.execution_date.localeCompare(b.execution_date);
const byValue = (dir) => (a, b) => {
  const va = priceOf(a);
  const vb = priceOf(b);
  if (va == null || vb == null) return (va == null) - (vb == null) || byDate(a, b);
  return (va - vb) * dir || byDate(a, b);
};
const byName = (dir) => (a, b) => a.client_name.localeCompare(b.client_name, "pt-BR", { sensitivity: "base" }) * dir || byDate(a, b);
const CMP = { data: byDate, "valor-asc": byValue(1), "valor-desc": byValue(-1), az: byName(1), za: byName(-1) };

export const sortServices = (list, mode) => [...list].sort(CMP[mode] || byDate);

const WIDE_COLS = "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_84px_112px_150px_20px]";

export const TableHead = ({ wide }) => (
  <div className={`hidden gap-x-3 pb-1 pl-11 pr-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${wide ? `lg:grid ${WIDE_COLS}` : ""}`}>
    <span>Cliente</span><span>Serviço</span><span>Data</span><span className="text-right">Valor</span><span>Situação</span><span />
  </div>
);

export const MonthTableRow = ({ s, wide, open, onToggle, checked, onCheck }) => {
  const { today } = useServices();
  const t = SERVICE_TYPES[s.service_type];
  const dot = <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: t?.hex || "#94A3B8" }} />;
  const money = <span className={`font-mono text-sm ${priceOf(s) == null ? "text-slate-500" : "text-slate-100"}`} data-testid={`month-row-value-${s.id}`}>{formatMoney(s.price)}</span>;
  return (
    <li className={`border-b border-slate-700/50 last:border-0 ${checked ? "bg-indigo-500/10" : ""}`} data-testid={`month-row-${s.id}`}>
      <div className="flex items-center">
      <Checkbox checked={checked} onCheckedChange={onCheck} className="ml-3 shrink-0 border-slate-500" aria-label={`Selecionar ${s.client_name}`} data-testid={`month-row-check-${s.id}`} />
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`month-row-toggle-${s.id}`}
        className={`grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-slate-800/70 ${wide ? WIDE_COLS : ""} ${open ? "bg-slate-800/70" : ""}`}>
        <span className="truncate font-semibold text-slate-100">{s.client_name}</span>
        <span className={`text-right ${wide ? "lg:hidden" : ""}`}>{money}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-slate-400 lg:text-sm">
          {dot}<span className="truncate">{s.service_name || t?.label}</span>
          <span className={`shrink-0 whitespace-nowrap font-mono ${wide ? "lg:hidden" : ""}`}>· {formatDate(s.execution_date).slice(0, 5)}</span>
        </span>
        <span className={`justify-self-end ${wide ? "lg:hidden" : ""}`}><StatusBadge s={s} today={today} /></span>
        {wide && <span className="hidden font-mono text-sm text-slate-300 lg:block">{formatDate(s.execution_date).slice(0, 5)}</span>}
        {wide && <span className="hidden text-right lg:block">{money}</span>}
        {wide && <span className="hidden lg:block"><StatusBadge s={s} today={today} /></span>}
        {wide && <ChevronDown className={`hidden h-4 w-4 text-slate-500 transition-transform duration-150 lg:block ${open ? "rotate-180" : ""}`} />}
      </button>
      </div>
      {open && <div className="px-2 pb-3 pt-1" data-testid={`month-row-details-${s.id}`}><ServiceRow s={s} /></div>}
    </li>
  );
};
